import React from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useTranslatedContent } from '../i18n/hooks/useTranslatedContent';
import ContactForm from './contact/ContactForm';
import { COMPANY_CONTACT } from '../constants/contact';

const Contact = () => {
  const { t } = useTranslatedContent('contact');

  return (
    <div id="contact" className="relative py-24 overflow-hidden">
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'30\' height=\'30\' viewBox=\'0 0 30 30\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Ccircle cx=\'1.22676\' cy=\'1.22676\' r=\'1.22676\' fill=\'rgba(255,255,255,0.1)\'/%3E%3C/svg%3E')] opacity-20" />
      </div>

      {/* Animated orbs */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-blue-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob" />
        <div className="absolute top-1/3 -right-48 w-96 h-96 bg-violet-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute -bottom-48 left-1/2 w-96 h-96 bg-indigo-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4">{t('title')}</h2>
          <p className="text-xl text-blue-100">
            {t('subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
            <ContactForm />
          </div>

          {/* Contact Information */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
            <div className="space-y-8">
              <div className="flex items-start group">
                <div className="flex-shrink-0">
                  <div className="p-4 bg-blue-500/10 rounded-xl group-hover:bg-blue-500/20 transition-colors duration-300">
                    <Mail className="h-6 w-6 text-blue-400" />
                  </div>
                </div>
                <div className="ml-6">
                  <h3 className="text-lg font-medium text-white mb-1">{t('contact.email')}</h3>
                  <a 
                    href={`mailto:${COMPANY_CONTACT.email}`} 
                    className="text-blue-200 hover:text-blue-100 transition-colors duration-300"
                  >
                    {COMPANY_CONTACT.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start group">
                <div className="flex-shrink-0">
                  <div className="p-4 bg-blue-500/10 rounded-xl group-hover:bg-blue-500/20 transition-colors duration-300">
                    <Phone className="h-6 w-6 text-blue-400" />
                  </div>
                </div>
                <div className="ml-6">
                  <h3 className="text-lg font-medium text-white mb-1">{t('contact.phone')}</h3>
                  <a 
                    href={`tel:${COMPANY_CONTACT.phone.replace(/\s/g, '')}`} 
                    className="text-blue-200 hover:text-blue-100 transition-colors duration-300"
                  >
                    {COMPANY_CONTACT.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start group">
                <div className="flex-shrink-0">
                  <div className="p-4 bg-blue-500/10 rounded-xl group-hover:bg-blue-500/20 transition-colors duration-300">
                    <MapPin className="h-6 w-6 text-blue-400" />
                  </div>
                </div>
                <div className="ml-6">
                  <h3 className="text-lg font-medium text-white mb-1">{t('contact.location')}</h3>
                  <p className="text-blue-200">
                    {COMPANY_CONTACT.address.street}<br />
                    {COMPANY_CONTACT.address.postalCode} {COMPANY_CONTACT.address.city}<br />
                    {COMPANY_CONTACT.address.country}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;